import { useParams } from "@tanstack/react-router"
import { type Locale, isLocale, useT } from "@/i18n"
import { invariant } from "@/utils"

/**
 * The current locale, never null. Public pages carry the (validated) locale in
 * the URL path; off those routes (the app, not prefixed) it is the active i18n
 * language, set by the route or the signed-in user.
 *
 * This is the single place i18n.language is narrowed to a Locale — it is always
 * one of ours (we configure no language detector and only changeLanguage to
 * supported values), so a mismatch is a "can't happen" we assert loudly rather
 * than silently coerce.
 */
export default function useLocale(): Locale {
  // useT (react-i18next) makes this reactive to changeLanguage.
  const { i18n } = useT()
  const { locale } = useParams({ strict: false })

  if (typeof locale === "string" && isLocale(locale)) {
    return locale
  }

  const language = i18n.language
  invariant(isLocale(language), `Unsupported i18n language: ${language}`)

  return language
}
